import { useEffect, useRef } from "react";

// Nya versioner når flikar som står öppna. En flik kör annars den kod som
// gällde när den laddades, i dagar om ingen laddar om den — även sedan vi
// rättat något (t.ex. att den ska sluta fråga servern när den ligger dold).
//
// Ungefär var tionde minut jämförs sidans eget bundle-namn med det Vercel
// serverar nu (ett anrop till Vercel, inte till databasen). Är det nytt laddas
// fliken om, men bara
//  - medan den är dold, och först efter 1–5 minuter (slumpat, så att alla
//    flikar inte laddar om samtidigt efter ett släpp),
//  - när inget ifyllt textfält skulle gå förlorat och skärmen inte säger
//    emot (canReload), och
//  - när webbläsaren är uppkopplad.
// En flik som syns laddas aldrig om.

const CHECK_EVERY = 10 * 60 * 1000;
const RELOAD_DELAY_MIN = 60 * 1000;
const RELOAD_DELAY_SPREAD = 4 * 60 * 1000;

const BUNDLE_RE = /\/static\/js\/main\.[0-9a-f]+\.js/;

function bundleIn(html) {
  const m = typeof html === "string" ? html.match(BUNDLE_RE) : null;
  return m ? m[0] : null;
}

function currentBundle(doc = document) {
  const src = [...doc.querySelectorAll("script[src]")]
    .map(el => el.getAttribute("src"))
    .find(s => BUNDLE_RE.test(s));
  return src ? src.match(BUNDLE_RE)[0] : null;
}

const TEXT_TYPES = new Set(["", "text", "search", "email", "url", "tel"]);

function hasUnsavedText(doc = document) {
  return [...doc.querySelectorAll("textarea, input")].some(el =>
    !el.readOnly && !el.disabled &&
    (el.tagName === "TEXTAREA" || TEXT_TYPES.has((el.getAttribute("type") || "").toLowerCase())) &&
    el.value.trim() !== "");
}

// Navigeringsliknande hämtning (Accept: text/html), så att service workern
// går mot nätet i stället för att svara med sin sparade kopia.
function fetchIndexHtml() {
  return fetch("/index.html", { cache: "no-store", headers: { Accept: "text/html" } })
    .then(r => (r.ok ? r.text() : ""));
}

function startVersionCheck({
  canReload = () => true,
  fetchHtml = fetchIndexHtml,
  reload = () => window.location.reload(),
  every = CHECK_EVERY,
} = {}) {
  if (typeof document === "undefined") return () => {};
  const mine = currentBundle();
  if (!mine) return () => {}; // utvecklingsserver: inget hashat bundle att jämföra

  let stopped = false;
  let stale = false;
  let checkTimer = null;
  let reloadTimer = null;
  const hidden = () => document.visibilityState === "hidden";
  const offline = () => typeof navigator !== "undefined" && navigator.onLine === false;

  function planReload() {
    clearTimeout(reloadTimer);
    if (stopped || !stale || !hidden()) return;
    reloadTimer = setTimeout(() => {
      if (stopped || !hidden()) return; // syns igen: vänta på nästa gång den döljs
      let ok = false;
      try { ok = !hasUnsavedText() && canReload() && !offline(); } catch { ok = false; }
      if (!ok) { planReload(); return; }
      reload();
    }, RELOAD_DELAY_MIN + Math.random() * RELOAD_DELAY_SPREAD);
  }

  async function check() {
    if (stopped) return;
    try {
      const latest = bundleIn(await fetchHtml());
      if (latest && latest !== mine) { stale = true; planReload(); return; }
    } catch { /* nätfel: försök igen nästa gång */ }
    scheduleCheck();
  }

  function scheduleCheck() {
    if (stopped) return;
    checkTimer = setTimeout(check, every * (0.8 + Math.random() * 0.4));
  }

  const onVisibility = () => { if (hidden()) planReload(); else clearTimeout(reloadTimer); };
  document.addEventListener("visibilitychange", onVisibility);
  scheduleCheck();

  return () => {
    stopped = true;
    clearTimeout(checkTimer);
    clearTimeout(reloadTimer);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

// canReload: skärmens eget veto, t.ex. "inte medan ett formulär är öppet".
// paused: en sovande flik (se useTabSleep) kontrollerar inget.
function useVersionCheck(canReload, paused = false) {
  const ref = useRef(canReload);
  ref.current = canReload;
  useEffect(() => {
    if (paused) return undefined;
    return startVersionCheck({ canReload: () => (ref.current ? ref.current() : true) });
  }, [paused]);
}

export { bundleIn, currentBundle, hasUnsavedText, startVersionCheck, useVersionCheck };
